/**
 * Core-100 CORE-BO — turn an opening into a ranked, explained list a person can act on.
 *
 * Pure. An opening (capacity_openings row) becomes a pull-forward trigger; every live ticket of the shop
 * is evaluated by the real planner (src/utils/pullForwardPlanner.ts). Eligible candidates come first,
 * strongest first; blocked ones keep their NAMED blockers ("a blocked candidate must carry a named
 * blocker, not silence"). Nothing here moves anything.
 */
import {
  rankPullForwardCandidates,
  type PullForwardCandidatePlan,
  type PullForwardTicketShape,
  type PullForwardTriggerKind,
} from '@/utils/pullForwardPlanner';
import { effectiveChainOwnerId, type ChainOwnableTicket } from '@/lib/scheduling/chainOwnership';
import { cultureBoolean } from '@/lib/shopCulture/shopCultureDefaults';

export interface CapacityOpeningRow {
  id: string;
  shop_id: string;
  cause: PullForwardTriggerKind;
  technician_id: string | null;
  bay_id: string | null;
  source_ticket_id: string | null;
  shop_day: string;
  window_start: string;
  window_end: string | null;
  available_minutes: number | null;
  effective_status: 'open' | 'filled' | 'dismissed' | 'expired';
  /** R4: someone has claimed this opening and is moving a ticket into it right now. */
  being_filled?: boolean | null;
}

export const OPENING_CAUSE_LABEL: Record<string, string> = {
  tech_completed_early: 'finished early',
  ticket_deleted_or_cancelled: 'a ticket was cancelled',
  tech_returned_from_absence: 'back from absence',
  tech_clocked_in_early: 'clocked in early',
  bay_lane_opened_early: 'a bay opened',
  hold_released: 'a hold was released',
  parking_ready_for_pull_in: 'a vehicle on site is ready to pull in',
};

/**
 * Causes whose source ticket IS the candidate (the released ticket, the vehicle ready to pull in). For every
 * other cause the source is the work that left (finished early, cancelled) and is never a candidate.
 */
export const SOURCE_IS_CANDIDATE: ReadonlySet<string> = new Set(['hold_released', 'parking_ready_for_pull_in']);

export const PULL_FORWARD_BLOCKER_LABEL: Record<string, string> = {
  terminal_state: 'already finished or closed',
  inactive_parking_lot: 'parked as inactive',
  estimate_not_converted: 'still an estimate',
  unresolved_hold: 'on hold',
  waiter_before_promise: 'a waiter cannot start before the time they were given',
  due_out_risk: 'would put its Due Out at risk',
  technician_unavailable: 'technician unavailable',
  technician_capacity_limited: 'technician has no room',
  bay_capacity_blocked: 'no bay free',
  bay_type_mismatch: 'needs a different bay',
  parking_to_bay_confirmation_required: 'needs confirmation that the vehicle can come in from parking',
  running_timer_constraint: 'work is already running',
  future_day_not_allowed: 'scheduled for a later day (shop setting: not allowed)',
  future_day_vehicle_not_present: 'scheduled for a later day and the vehicle is not here',
  vehicle_not_present: 'the vehicle is not here yet (it stays in the plan, but cannot be pulled forward)',
  missing_schedule_window: 'no schedule window',
  no_assigned_tech: 'no technician assigned',
  ticket_locked: 'locked to its technician',
  ticket_pinned: 'pinned',
  not_a_pull_forward_candidate: 'not a candidate',
};

/**
 * oct20e — the shop's capacity-recovery policy as every surface reads it (CORE-BI shop culture; FD-2026-10-24-A;
 * CORE-BO :74 as amended). Both default to TRUE when the shop has stored nothing: automatic recovery is the Core
 * default, and future-day pull-forward of physically present vehicles is permitted unless the shop restricts it.
 */
export function capacityRecoveryPolicy(culture: unknown): { automatic: boolean; allowFutureDay: boolean } {
  return {
    automatic: cultureBoolean(culture, 'capacity_recovery_automatic'),
    allowFutureDay: cultureBoolean(culture, 'allow_future_day_pull_forward'),
  };
}

/** An opening the automatic recovery filled (capacity_openings, resolution_mode 'automatic'). */
export interface AutoFilledOpeningRow {
  id: string;
  cause: string;
  technician_id: string | null;
  filled_ticket_id: string | null;
  resolved_at: string | null;
  recovered_minutes: number | null;
}

/** Plain words for an automatic fill: what moved, into whose time, and why. */
export function describeAutoFill(row: AutoFilledOpeningRow, ticketName: string, techName: string | null, timeZone: string): string {
  const at = row.resolved_at ? new Date(row.resolved_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone }) : '';
  const minutes = row.recovered_minutes != null ? ` (${row.recovered_minutes} min recovered)` : '';
  return `${ticketName} moved into ${techName ?? 'a technician'}'s opening${at ? ` at ${at}` : ''} — ${OPENING_CAUSE_LABEL[row.cause] ?? row.cause}${minutes}. The vehicle was at the shop and nothing promised moved.`;
}

export function planOpening(
  opening: CapacityOpeningRow,
  tickets: ReadonlyArray<PullForwardTicketShape & { id: string }>,
  opts: { now: Date; allowFutureDay: boolean },
): PullForwardCandidatePlan[] {
  const candidates = SOURCE_IS_CANDIDATE.has(opening.cause) ? tickets : tickets.filter((t) => t.id !== opening.source_ticket_id);
  return rankPullForwardCandidates(candidates, {
    trigger: {
      kind: opening.cause,
      occurredAt: new Date(opening.window_start),
      techId: opening.technician_id,
      bayId: opening.bay_id,
      availableMinutes: opening.available_minutes ?? undefined,
      windowStart: new Date(opening.window_start),
      windowEnd: opening.window_end ? new Date(opening.window_end) : undefined,
      shopDay: opening.shop_day,
    },
    now: opts.now,
    policy: { allowFutureDay: opts.allowFutureDay, allowWaiterBeforePromise: false },
  });
}

/**
 * The ticket columns the pull-forward planner reads — ONE list for every surface that plans an opening (the Open
 * capacity panel and, Core-100 R4 W5, the "Promises in trouble" link below).
 */
export const OPEN_CAPACITY_TICKET_COLUMNS =
  'id, status, type, due_out, scheduled_date, scheduled_start_time, start_time, assigned_tech_id, scheduled_tech_id, parking_lot, parking_spot_id, bay_id, timer_status, is_estimate, deleted_at, customer_name, predicted_minutes, actually_started, completed_at, picked_up_at';

export interface PromiseOpeningMatch {
  opening: CapacityOpeningRow;
  plan: PullForwardCandidatePlan;
}

/**
 * Core-100 R4 W5 — CORE-BH GAP-006 / HARD-007 (pull-forward as a due-out mitigation, CORE-BO): which of today's open
 * openings could take THIS promise's ticket. Not a second planner: every opening is evaluated by the same
 * `planOpening` the Open capacity panel uses (physical > Due Out > waiter precedence, named blockers), and only an
 * ELIGIBLE plan for this ticket counts. An opening someone is already filling is left out; an opening that starts
 * after the promised time cannot help the promise and is left out too. Suggestion only — nothing moves here.
 */
export function openingsThatCouldTakeTicket(
  ticketId: string,
  promisedAt: string | null,
  openings: ReadonlyArray<CapacityOpeningRow>,
  tickets: ReadonlyArray<PullForwardTicketShape & { id: string }>,
  opts: { now: Date; allowFutureDay: boolean },
): PromiseOpeningMatch[] {
  const promised = promisedAt ? new Date(promisedAt).getTime() : Number.POSITIVE_INFINITY;
  const out: PromiseOpeningMatch[] = [];
  for (const opening of openings) {
    if (opening.effective_status !== 'open' || opening.being_filled) continue;
    if (new Date(opening.window_start).getTime() >= promised) continue;
    const plan = planOpening(opening, tickets, opts).find((p) => p.ticketId === ticketId);
    if (plan?.eligible) out.push({ opening, plan });
  }
  return out.sort((a, b) => new Date(a.opening.window_start).getTime() - new Date(b.opening.window_start).getTime());
}

/** Plain words for the link: who opened up, from when, and why — and that a person decides in Open capacity. */
export function describePromiseOpening(m: PromiseOpeningMatch, techName: string | null, timeZone: string): string {
  const from = new Date(m.opening.window_start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone });
  const minutes = m.opening.available_minutes != null ? ` (${m.opening.available_minutes} min)` : '';
  return `Capacity opened: ${techName ?? 'a technician'} from ${from}${minutes} — ${OPENING_CAUSE_LABEL[m.opening.cause] ?? m.opening.cause}. It could take this work: see Open capacity to pull it forward.`;
}

export function describeBlockers(plan: PullForwardCandidatePlan): string {
  return plan.blockers.map((b) => PULL_FORWARD_BLOCKER_LABEL[b] ?? b).join('; ');
}

/** The minimal client surface the fill needs (member calls on a real SupabaseClient — never detached). */
export interface FillClient {
  rpc: (fn: string, args: Record<string, unknown>) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
  functions: { invoke: (fn: string, opts: { body: Record<string, unknown> }) => Promise<{ data: unknown; error: unknown }> };
}

export type FillOutcome =
  | { ok: true; resolved: true }
  | { ok: true; resolved: false }                       // moved, but the opening could not be marked filled (claim kept)
  | { ok: false; stage: 'claim'; message: string }      // someone else is filling it / it is no longer open: nothing moved
  | { ok: false; stage: 'move'; error: unknown };       // the move was refused: nothing moved, claim released

/**
 * Core-100 R4 CORE-BO — filling an opening is ONE person's decision, applied once:
 *   1. claim the opening for this ticket (refused while someone else is filling it, or once it is resolved);
 *   2. move the ticket through reassign-ticket (its own gating; reason `capacity_recovery_pull_forward`);
 *   3. resolve the opening as filled (writes CAPACITY_OPENING_FILLED with the opening id and cause) —
 *      or, when the move is refused, release the claim so the opening stays open for someone else.
 * The panel used to move first and resolve second with no claim: two people could both move work into one
 * opening, and a refused move could not be told apart from a resolved one.
 */
export async function fillOpening(
  client: FillClient,
  args: { openingId: string; ticketId: string; targetTechId: string; scheduledDate: string; windowStart: string },
): Promise<FillOutcome> {
  const claim = await client.rpc('claim_capacity_opening', { p_opening_id: args.openingId, p_ticket_id: args.ticketId });
  if (claim.error) return { ok: false, stage: 'claim', message: claim.error.message };
  const move = await client.functions.invoke('reassign-ticket', {
    body: { ticketId: args.ticketId, targetTechId: args.targetTechId, scheduledDate: args.scheduledDate,
      desiredStartTime: args.windowStart, manualOverride: true, moveReason: 'capacity_recovery_pull_forward' },
  });
  if (move.error) {
    await client.rpc('release_capacity_opening_claim', { p_opening_id: args.openingId });
    return { ok: false, stage: 'move', error: move.error };
  }
  const resolved = await client.rpc('resolve_capacity_opening', { p_opening_id: args.openingId, p_outcome: 'filled', p_filled_ticket_id: args.ticketId, p_reason: null });
  return resolved.error ? { ok: true, resolved: false } : { ok: true, resolved: true };
}

/**
 * oct20d auditfix2 — whose lane the pulled-forward work joins. An opening that names a technician decides it. A bay
 * opening may name none: the candidate keeps its own technician — its chain owner (assigned, or parked-and-scheduled),
 * because a checked-in (parked) car has no assigned_tech_id and only its plan (scheduled_tech_id) names its lane.
 * Null = no lane is known, and nothing is moved.
 */
export function fillTargetTechId(opening: Pick<CapacityOpeningRow, 'technician_id'> | null, candidate: ChainOwnableTicket | null | undefined): string | null {
  return opening?.technician_id ?? effectiveChainOwnerId(candidate);
}

/** Plain words for a refused claim. */
export function describeClaimRefusal(message: string): string {
  if (/LANE_BEING_FILLED/.test(message)) return "Another opening of this technician's time is being filled right now; nothing moved. Try again in a moment.";
  if (/BEING_FILLED/.test(message)) return 'Someone else is filling this opening right now.';
  if (/NOT_OPEN/.test(message)) return 'This opening was already filled or left unfilled.';
  if (/VEHICLE_NOT_PRESENT/.test(message)) return 'This later-day vehicle is not at the shop, so it cannot fill this opening; nothing moved.';
  return 'This opening could not be claimed; nothing moved.';
}
